import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useChat } from '../hooks/useChat';
import { useDashboard } from '../hooks/useDashboard';
import { useSettings } from '../hooks/useSettings';
import { useTranslation } from '../hooks/useTranslation';
import vegaEmbed from 'vega-embed';
import DatePresets from './DatePresets';
import DashboardSettings from './DashboardSettings';
import ExportButton from './ExportButton';
import PeriodComparison from './PeriodComparison';
import DataQualityInsights from './DataQualityInsights';
import MyChartsSection from './MyChartsSection';
import {
  Database,
  TrendingUp,
  Calendar,
  Layers,
  PieChart,
  BarChart3,
  Activity,
  Info,
  ArrowRight,
  BookOpen,
  GitBranch,
  Hash,
  Truck,
  CreditCard,
  MapPin,
  AlertCircle,
  Clock,
  Award,
  Maximize2,
  X,
  Filter,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Users,
  DollarSign,
  CheckCircle2,
  Zap,
  Settings,
  Globe
} from 'lucide-react';

const DashboardContainer = styled.div`
  flex: 1;
  height: 100vh;
  overflow-y: auto;
  padding: 32px;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
`;

const DashboardHeader = styled.div`
  margin-bottom: 32px;
`;

const DashboardTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #1A8B8B;
  }
`;

const DashboardSubtitle = styled.p`
  font-size: 16px;
  color: #718096;
  line-height: 1.6;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
    border-color: #3b82f6;
    color: #3b82f6;
  }
`;

const LanguageToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
    border-color: #3b82f6;
    color: #3b82f6;
  }
`;

const DateFilterSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e2e8f0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled(motion.div)`
  background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 16px rgba(26, 139, 139, 0.3);
  border: none;
  transition: all 0.3s ease;
  color: white;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(26, 139, 139, 0.4);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const StatLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: white;
  margin-bottom: 8px;
`;

const StatDescription = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #1A8B8B;
    width: 20px;
    height: 20px;
  }
`;

const DataModelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
`;

const DataModelItem = styled.div`
  padding: 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border: 2px solid #e2e8f0;

  .label {
    font-size: 12px;
    font-weight: 600;
    color: #1A8B8B;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
  }

  .value {
    font-size: 16px;
    font-weight: 500;
    color: #2d3748;
    margin-bottom: 4px;
  }

  .description {
    font-size: 13px;
    color: #718096;
    line-height: 1.5;
  }
`;

const InfoBox = styled.div`
  padding: 16px;
  border-radius: 12px;
  background: #edf2f7;
  border-left: 4px solid #1A8B8B;
  margin-top: 20px;
  display: flex;
  align-items: start;
  gap: 12px;

  svg {
    color: #1A8B8B;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .content {
    font-size: 14px;
    color: #4a5568;
    line-height: 1.6;

    strong {
      color: #2d3748;
      font-weight: 600;
    }
  }
`;

const WarningBox = styled(InfoBox)`
  background: #fef5e7;
  border-left-color: #f59e0b;

  svg {
    color: #f59e0b;
  }
`;

const ComparisonSection = styled.div`
  background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  color: white;
  box-shadow: 0 8px 16px rgba(26, 139, 139, 0.2);
`;

const ComparisonTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const LookupTableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const LookupTable = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: blur(10px);

  .table-name {
    font-size: 16px;
    font-weight: 600;
    color: #fbbf24;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .table-description {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 12px;
    line-height: 1.5;
  }

  .values-list {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    padding: 12px;
    max-height: 200px;
    overflow-y: auto;

    .value-item {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.95);
      padding: 4px 8px;
      margin: 2px 0;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
      font-family: 'Monaco', 'Menlo', monospace;
      display: flex;
      align-items: center;
      gap: 8px;

      .id {
        color: #fbbf24;
        font-weight: 600;
        min-width: 30px;
      }

      .arrow {
        color: rgba(255, 255, 255, 0.5);
      }

      .text {
        flex: 1;
      }
    }

    .total-count {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.7);
      margin-top: 8px;
      font-style: italic;
      text-align: center;
    }
  }
`;

const NullPolicySection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const NullPolicyTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #1A8B8B;
    width: 28px;
    height: 28px;
  }
`;

const NullFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 20px;
`;

const NullFieldCard = styled.div`
  background: linear-gradient(135deg, #fef5e7 0%, #fef9e6 100%);
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #f59e0b;

  .field-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;

    svg {
      color: #f59e0b;
      width: 20px;
      height: 20px;
    }

    h3 {
      font-size: 16px;
      font-weight: 600;
      color: #2d3748;
      margin: 0;
    }
  }

  .null-percentage {
    font-size: 14px;
    color: #d97706;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .field-explanation {
    font-size: 14px;
    color: #4a5568;
    line-height: 1.6;
    margin-bottom: 12px;
  }

  .example {
    background: white;
    padding: 12px;
    border-radius: 8px;
    font-size: 13px;
    color: #2d3748;
    margin-top: 8px;

    .example-label {
      font-size: 11px;
      font-weight: 600;
      color: #f59e0b;
      text-transform: uppercase;
      margin-bottom: 6px;
    }

    .example-text {
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 12px;
      line-height: 1.6;
    }
  }
`;

const DataGenerationSection = styled.div`
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  color: white;
  box-shadow: 0 8px 16px rgba(99, 102, 241, 0.2);
`;

const DataGenTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const StrategyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
`;

const StrategyCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: blur(10px);

  .strategy-title {
    font-size: 16px;
    font-weight: 600;
    color: white;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 20px;
      height: 20px;
      color: #fbbf24;
    }
  }

  .strategy-description {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.6;
    margin-bottom: 16px;
  }

  .distribution-list {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    padding: 12px;

    .dist-item {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.95);
      padding: 4px 8px;
      margin: 2px 0;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
      font-family: 'Monaco', 'Menlo', monospace;
    }
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(max(380px, calc(50% - 12px)), 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

// KPI Summary Cards Styling
const KpiCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const KpiCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const KpiIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$gradient || 'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)'};
  color: white;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const KpiContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const KpiValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.2;
`;

const KpiLabel = styled.div`
  font-size: 13px;
  color: #718096;
  margin-top: 4px;
`;

const KpiDescription = styled.div`
  font-size: 11px;
  color: #a0aec0;
  margin-top: 4px;
  line-height: 1.4;

  .details {
    font-size: 10px;
    color: #cbd5e0;
    margin-top: 2px;
    word-break: break-word;
  }
`;

// Peak Hours Styling
const PeakHoursSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const PeakHoursTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #1A8B8B;
    width: 20px;
    height: 20px;
  }
`;

const PeakHoursGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const PeakHourCard = styled.div`
  background: linear-gradient(135deg, ${props => props.$isTop ? '#1A8B8B' : '#f7fafc'} 0%, ${props => props.$isTop ? '#0D6B6B' : '#edf2f7'} 100%);
  border-radius: 12px;
  padding: 16px 20px;
  min-width: 140px;
  text-align: center;
  border: ${props => props.$isTop ? 'none' : '1px solid #e2e8f0'};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .hour {
    font-size: 20px;
    font-weight: 700;
    color: ${props => props.$isTop ? 'white' : '#2d3748'};
    margin-bottom: 4px;
  }

  .count {
    font-size: 14px;
    color: ${props => props.$isTop ? 'rgba(255, 255, 255, 0.9)' : '#4a5568'};
    margin-bottom: 2px;
  }

  .percentage {
    font-size: 12px;
    color: ${props => props.$isTop ? 'rgba(255, 255, 255, 0.8)' : '#718096'};
  }

  .badge {
    display: inline-block;
    background: rgba(255, 255, 255, 0.2);
    color: white;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    margin-top: 6px;
    text-transform: uppercase;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  min-width: 0;

  .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .chart-title {
    font-size: 18px;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      color: #1A8B8B;
      width: 20px;
      height: 20px;
    }
  }

  .chart-container {
    width: 100%;
    min-width: 200px;
    min-height: 300px;
    /* Ensure container is laid out before Vega renders */
    contain: layout;
  }
`;

const FullscreenButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #718096;

  &:hover {
    background: #f7fafc;
    border-color: #1A8B8B;
    color: #1A8B8B;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 40px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 90vw;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;

  .modal-title {
    font-size: 20px;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 12px;

    svg {
      color: #1A8B8B;
      width: 24px;
      height: 24px;
    }
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: #f7fafc;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #718096;

  &:hover {
    background: #edf2f7;
    color: #2d3748;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  flex: 1;
  overflow: auto;

  .fullscreen-chart-container {
    width: 100%;
    min-height: 500px;
  }
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const RebuildSection = styled.div`
  background: ${props => props.$active
    ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
    : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'};
  border-radius: 12px;
  padding: 14px 20px;
  margin-bottom: 24px;
  border: 1px solid ${props => props.$active ? '#e2e8f0' : '#fcd34d'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .rebuild-info {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .title {
      font-size: 13px;
      font-weight: 600;
      color: #475569;
      display: flex;
      align-items: center;
      gap: 8px;
      letter-spacing: -0.01em;
    }

    .status {
      font-size: 13px;
      color: ${props => props.$active ? '#059669' : '#d97706'};
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 6px;

      .status-icon {
        &.active { color: #059669; }
        &.inactive { color: #d97706; }
      }
    }

    .last-rebuild {
      font-size: 11px;
      color: #94a3b8;

      .duration {
        margin-left: 4px;
        color: #cbd5e1;
      }
    }
  }
`;

const RebuildButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  background: white;
  color: #475569;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #334155;
  }

  &:active:not(:disabled) {
    background: #f1f5f9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
    color: #64748b;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const RefreshButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const QuickRefreshButton = styled(RebuildButton)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    color: white;
  }

  svg {
    color: white;
  }
`;

const ScheduledJobsInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${props => props.$active ? '#e2e8f0' : '#fcd34d'};
  font-size: 11px;
  color: #64748b;

  .job-item {
    display: flex;
    align-items: center;
    gap: 6px;

    svg {
      width: 12px;
      height: 12px;
      color: #94a3b8;
    }

    .job-name {
      color: #475569;
      font-weight: 500;
    }

    .job-time {
      color: #94a3b8;
    }
  }
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  .filter-title {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      color: #1A8B8B;
      width: 18px;
      height: 18px;
    }
  }

  .filter-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const FilterGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12px;
    font-weight: 500;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  input, select {
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    color: #2d3748;
    background: white;
    min-width: 150px;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #1A8B8B;
      box-shadow: 0 0 0 3px rgba(26, 139, 139, 0.1);
    }
  }
`;

const MonthCheckboxGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-width: 400px;
`;

const MonthCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  background: ${props => props.checked ? 'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)' : '#f7fafc'};
  color: ${props => props.checked ? 'white' : '#4a5568'};
  border: 1px solid ${props => props.checked ? 'transparent' : '#e2e8f0'};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.checked ? 'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)' : '#edf2f7'};
  }

  input {
    display: none;
  }
`;

const ChartSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${props => props.filtered ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' : 'white'};
  border: 1px solid ${props => props.filtered ? '#0ea5e9' : '#e2e8f0'};
  box-shadow: ${props => props.filtered ? '0 4px 12px rgba(14, 165, 233, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.05)'};
  transition: all 0.3s ease;

  .section-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.filtered ? 'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)' : 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'};
    color: white;
    box-shadow: ${props => props.filtered ? '0 4px 12px rgba(26, 139, 139, 0.3)' : '0 2px 8px rgba(72, 187, 120, 0.3)'};

    svg {
      width: 22px;
      height: 22px;
    }
  }

  .section-info {
    flex: 1;

    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #2d3748;
      margin: 0 0 4px 0;
    }

    p {
      font-size: 13px;
      color: ${props => props.filtered ? '#0369a1' : '#718096'};
      margin: 0;
      font-weight: ${props => props.filtered ? '500' : 'normal'};
    }
  }

  .section-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    background: ${props => props.filtered ? 'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)' : '#48bb78'};
    color: white;
    box-shadow: ${props => props.filtered ? '0 2px 8px rgba(26, 139, 139, 0.3)' : 'none'};
    animation: ${props => props.filtered ? 'pulse 2s ease-in-out infinite' : 'none'};

    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &.primary {
    background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
    color: white;
    border: none;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }
  }

  &.secondary {
    background: white;
    color: #718096;
    border: 1px solid #e2e8f0;

    &:hover:not(:disabled) {
      background: #f7fafc;
      color: #2d3748;
    }
  }

  &.reset-btn {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border: none;
    padding: 10px 20px;
    font-weight: 600;

    &:hover:not(:disabled) {
      opacity: 0.9;
      transform: translateY(-1px);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActiveFiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #0ea5e9;

  .active-filters-label {
    font-size: 13px;
    font-weight: 600;
    color: #0369a1;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-${props => props.theme?.isRTL ? 'left' : 'right'}: 8px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const FilterTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
  color: white;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(26, 139, 139, 0.3);

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
    transition: all 0.2s ease;
    margin-${props => props.theme?.isRTL ? 'right' : 'left'}: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.4);
      transform: scale(1.1);
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

// Helper function to add tooltip to encoding
function addTooltipToEncoding(encoding, data) {
  if (!encoding || !data || data.length === 0) return encoding;

  // Get all field names from the data
  const fields = Object.keys(data[0] || {});

  // Create tooltip array from all fields
  const tooltipFields = fields.map(field => ({
    field: field,
    type: typeof data[0][field] === 'number' ? 'quantitative' : 'nominal',
    title: field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }));

  return {
    ...encoding,
    tooltip: tooltipFields
  };
}

// Helper function to detect if a value looks like a date
function isDateLikeValue(value) {
  if (!value) return false;
  const strVal = String(value);
  // Check for common date patterns: YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, ISO dates, etc.
  const datePatterns = [
    /^\d{4}-\d{2}-\d{2}/, // YYYY-MM-DD
    /^\d{2}\/\d{2}\/\d{4}/, // DD/MM/YYYY or MM/DD/YYYY
    /^\d{4}\/\d{2}\/\d{2}/, // YYYY/MM/DD
    /^\d{1,2}-\d{1,2}-\d{4}/, // D-M-YYYY
  ];
  if (datePatterns.some(pattern => pattern.test(strVal))) {
    const parsed = new Date(value);
    return !isNaN(parsed.getTime());
  }
  return false;
}

// Helper function to detect if a field name suggests it's a date field
function isDateFieldName(fieldName) {
  const lowerName = fieldName.toLowerCase();
  // Note: 'week' and 'month' are NOT included here because they are period identifiers (W01-2024, 2024-01)
  // not actual date values. Only fields with actual date values should be detected.
  const dateKeywords = ['date', 'time', 'created', 'updated', 'timestamp', 'at', 'transaction_date', 'record_date'];
  // Exclude period identifiers
  if (lowerName === 'week' || lowerName === 'month' || lowerName === 'year' || lowerName === 'quarter') {
    return false;
  }
  return dateKeywords.some(keyword => lowerName.includes(keyword));
}

// Helper function to extract month from various formats
function extractMonth(value) {
  if (value === null || value === undefined) return null;

  // If it's a number between 1-12, return it
  if (typeof value === 'number' && value >= 1 && value <= 12) {
    return value;
  }

  const strVal = String(value);

  // Try to extract number from string like "Month 3", "3", "03"
  const numMatch = strVal.match(/(\d+)/);
  if (numMatch) {
    const num = parseInt(numMatch[1]);
    if (num >= 1 && num <= 12) return num;
  }

  // Try to parse as date and get month
  const dateVal = new Date(value);
  if (!isNaN(dateVal.getTime())) {
    return dateVal.getMonth() + 1; // getMonth() returns 0-11
  }

  return null;
}

// Helper function to apply date filter to chart data
function applyDateFilter(data, globalFilters) {
  if (!data || !globalFilters || data.length === 0) return data;

  // Auto-detect date fields from the first item
  const firstItem = data[0];
  const dateFields = Object.keys(firstItem).filter(field => {
    // Check if field name suggests date
    if (isDateFieldName(field)) {
      // Verify by checking if value is date-like
      return isDateLikeValue(firstItem[field]);
    }
    // Also check if value looks like a date even if field name doesn't suggest it
    return isDateLikeValue(firstItem[field]);
  });

  // Auto-detect month fields (fields with values 1-12 or month-like names)
  const monthFields = Object.keys(firstItem).filter(field => {
    const lowerName = field.toLowerCase();
    if (lowerName.includes('month')) return true;
    // Check if values are month-like (1-12)
    const val = firstItem[field];
    if (typeof val === 'number' && val >= 1 && val <= 12) return true;
    return false;
  });

  return data.filter(item => {
    // Apply date range filters on detected date fields
    if (globalFilters.startDate || globalFilters.endDate) {
      for (const field of dateFields) {
        if (item[field]) {
          const dateValue = new Date(item[field]);
          if (!isNaN(dateValue.getTime())) {
            if (globalFilters.startDate && dateValue < new Date(globalFilters.startDate)) {
              return false;
            }
            if (globalFilters.endDate && dateValue > new Date(globalFilters.endDate)) {
              return false;
            }
          }
        }
      }
    }

    // Apply months filter on detected month fields or extract from date fields
    const selectedMonths = globalFilters.months || [];
    if (selectedMonths.length > 0) {
      // First try dedicated month fields
      for (const field of monthFields) {
        const itemMonth = extractMonth(item[field]);
        if (itemMonth !== null) {
          if (!selectedMonths.includes(String(itemMonth))) {
            return false;
          }
          return true; // Found a match, no need to check further
        }
      }

      // If no month field, try to extract month from date fields
      for (const field of dateFields) {
        if (item[field]) {
          const dateValue = new Date(item[field]);
          if (!isNaN(dateValue.getTime())) {
            const itemMonth = dateValue.getMonth() + 1;
            if (!selectedMonths.includes(String(itemMonth))) {
              return false;
            }
            return true;
          }
        }
      }
    }

    return true;
  });
}

// Chart component to render individual charts
function Chart({ title, description, chartData, icon: Icon, globalFilters, chartFilter, onChartFilterChange, t, language }) {
  const chartRef = useRef(null);
  const fullscreenChartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showChartFilter, setShowChartFilter] = useState(false);
  const [isChartRendered, setIsChartRendered] = useState(false);
  const [renderError, setRenderError] = useState(null);

  // Reset rendered state when chartData changes
  useEffect(() => {
    setIsChartRendered(false);
    setRenderError(null);
  }, [chartData]);

  // Get filtered data
  const getFilteredData = () => {
    if (!chartData || !chartData.data) {
      return [];
    }
    let filtered = [...chartData.data];

    // Apply global filters
    const hasDateFilters = globalFilters && (
      globalFilters.startDate ||
      globalFilters.endDate ||
      (globalFilters.months && globalFilters.months.length > 0)
    );
    if (hasDateFilters) {
      filtered = applyDateFilter(filtered, globalFilters);
    }

    // Apply chart-specific filter (supports multiple values and ranges)
    const hasChartFilter = chartFilter && chartFilter.field && (
      chartFilter.value ||
      (chartFilter.values && chartFilter.values.length > 0) ||
      chartFilter.rangeMin ||
      chartFilter.rangeMax
    );

    if (hasChartFilter) {
      filtered = filtered.filter(item => {
        const itemValue = item[chartFilter.field];
        const itemValueStr = String(itemValue || '');

        // Range filter for numeric values
        if (chartFilter.rangeMin !== undefined && chartFilter.rangeMin !== '' ||
            chartFilter.rangeMax !== undefined && chartFilter.rangeMax !== '') {

          // Check if it's a date range
          const isDateRange = String(chartFilter.rangeMin || chartFilter.rangeMax).match(/^\d{4}-\d{2}-\d{2}/);

          if (isDateRange) {
            const itemDate = new Date(itemValue);
            if (isNaN(itemDate.getTime())) return true; // Skip if not a valid date

            if (chartFilter.rangeMin) {
              const minDate = new Date(chartFilter.rangeMin);
              if (itemDate < minDate) return false;
            }
            if (chartFilter.rangeMax) {
              const maxDate = new Date(chartFilter.rangeMax);
              if (itemDate > maxDate) return false;
            }
            return true;
          } else {
            // Numeric range
            const numValue = Number(itemValue);
            if (isNaN(numValue)) return true; // Skip if not a number

            if (chartFilter.rangeMin !== undefined && chartFilter.rangeMin !== '') {
              if (numValue < Number(chartFilter.rangeMin)) return false;
            }
            if (chartFilter.rangeMax !== undefined && chartFilter.rangeMax !== '') {
              if (numValue > Number(chartFilter.rangeMax)) return false;
            }
            return true;
          }
        }

        // If we have an array of values, check if item matches any
        if (chartFilter.values && chartFilter.values.length > 0) {
          return chartFilter.values.includes(itemValueStr);
        }

        // Fallback to text contains search
        if (chartFilter.value) {
          return itemValueStr.toLowerCase().includes(String(chartFilter.value).toLowerCase());
        }

        return true;
      });
    }

    return filtered;
  };

  // Render chart function
  const renderChart = (container, height = 300) => {
    const filteredData = getFilteredData();

    if (container && filteredData.length > 0) {
      // Add tooltip to encoding
      const enhancedEncoding = addTooltipToEncoding(chartData.encoding, filteredData);

      // RTL configuration for Arabic
      const isRTL = language === 'ar';

      // Adjust encoding for RTL - swap x-axis sort direction for bar charts
      let rtlEnhancedEncoding = { ...enhancedEncoding };
      if (isRTL && chartData.mark === 'bar' && rtlEnhancedEncoding.x && rtlEnhancedEncoding.x.sort) {
        // Reverse sort for RTL so bars go right-to-left
        rtlEnhancedEncoding.x = {
          ...rtlEnhancedEncoding.x,
          sort: rtlEnhancedEncoding.x.sort === 'ascending' ? 'descending' :
                rtlEnhancedEncoding.x.sort === 'descending' ? 'ascending' : rtlEnhancedEncoding.x.sort
        };
      }

      const spec = {
        $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
        data: { values: filteredData },
        mark: chartData.mark,
        encoding: rtlEnhancedEncoding,
        width: 'container',
        height: height,
        config: {
          view: { stroke: null },
          axis: {
            labelFontSize: 11,
            titleFontSize: 12,
            labelLimit: 120,
            titlePadding: 10
          },
          axisX: {
            labelAngle: -45,
            labelAlign: 'right',
            labelOverlap: 'parity'
          },
          axisY: {
            orient: isRTL ? 'right' : 'left',
            titlePadding: 20
          },
          legend: {
            labelLimit: 160,
            labelFontSize: 11,
            titleFontSize: 11,
            symbolSize: 100,
            columnPadding: 10,
            rowPadding: 4,
            labelOverlap: 'parity'
          }
        }
      };

      vegaEmbed(container, spec, { actions: false }).then(() => {
        setIsChartRendered(true);
        setRenderError(null);
      }).catch(err => {
        console.error('Vega render error:', err);
        setRenderError(err.message || 'Chart rendering failed');
        setIsChartRendered(true);
      });
    } else if (container) {
      // Show no data message
      const noDataMsg = t ? t('chart.noData') : (language === 'ar' ? 'لا توجد بيانات تطابق الفلاتر الحالية' : 'No data matches the current filters');
      container.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #718096; font-size: 14px;">${noDataMsg}</div>`;
      setIsChartRendered(true);
    }
  };

  // Track container width for visibility detection
  const lastWidthRef = useRef(0);

  // ResizeObserver handles tab visibility changes (display:none -> display:flex)
  useEffect(() => {
    const container = chartRef.current;
    if (!container) return;

    // Render when container has width
    const tryRender = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0) {
        renderChart(container, 300);
        lastWidthRef.current = rect.width;
      }
    };

    // Try immediately
    tryRender();

    // Observer detects tab switches (width: 0 -> positive)
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        // Container just became visible
        if (newWidth > 0 && lastWidthRef.current === 0) {
          tryRender();
        }
        lastWidthRef.current = newWidth;
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [chartData, globalFilters, chartFilter, language]);

  useEffect(() => {
    if (isFullscreen && fullscreenChartRef.current) {
      // Small delay for fullscreen modal to render
      const timeoutId = setTimeout(() => {
        renderChart(fullscreenChartRef.current, 500);
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isFullscreen, chartData, globalFilters, chartFilter, language]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
        setShowChartFilter(false);
      }
    };
    if (isFullscreen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isFullscreen]);

  // Get available fields for filtering
  const availableFields = chartData?.data?.[0]
    ? Object.keys(chartData.data[0]).filter(f => f !== 'count' && f !== 'percentage')
    : [];

  const filteredCount = getFilteredData().length;
  const totalCount = chartData?.data?.length || 0;

  return (
    <>
      <ChartCard>
        <div className="chart-header">
          <div className="chart-title">
            {Icon && <Icon />}
            <div>
              <div>{title}</div>
              {description && <div style={{ fontSize: '12px', fontWeight: 'normal', color: '#718096', marginTop: '2px' }}>{description}</div>}
            </div>
            {(chartFilter?.value || (chartFilter?.values && chartFilter.values.length > 0) || chartFilter?.rangeMin || chartFilter?.rangeMax) && (
              <span style={{
                fontSize: '11px',
                background: 'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)',
                color: 'white',
                padding: '4px 10px',
                borderRadius: '16px',
                marginInlineStart: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 6px rgba(26, 139, 139, 0.3)'
              }}>
                {language === 'ar' ? 'مفلتر' : 'Filtered'}
                {chartFilter?.values?.length > 0 && ` (${chartFilter.values.length})`}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onChartFilterChange?.(null);
                  }}
                  title={language === 'ar' ? 'مسح فلتر الرسم' : 'Clear chart filter'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.25)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    padding: '2px',
                    borderRadius: '50%',
                    transition: 'all 0.2s ease',
                    marginInlineStart: '2px'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.4)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)'}
                >
                  <X size={12} />
                </button>
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <FullscreenButton
              onClick={() => setShowChartFilter(!showChartFilter)}
              title={language === 'ar' ? 'تصفية الرسم' : 'Filter this chart'}
              style={showChartFilter ? { borderColor: '#1A8B8B', color: '#1A8B8B' } : {}}
            >
              <Filter />
            </FullscreenButton>
            <FullscreenButton
              onClick={() => setIsFullscreen(true)}
              title={language === 'ar' ? 'ملء الشاشة' : 'View fullscreen'}
            >
              <Maximize2 />
            </FullscreenButton>
          </div>
        </div>

        {/* Per-chart filter UI */}
        {showChartFilter && (
          <div style={{
            padding: '12px',
            background: '#f7fafc',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: '#718096', textTransform: 'uppercase' }}>
                {language === 'ar' ? 'الحقل' : 'Field'}
              </label>
              <select
                value={chartFilter?.field || ''}
                onChange={(e) => onChartFilterChange?.({ field: e.target.value, value: '' })}
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  fontSize: '13px',
                  minWidth: '120px'
                }}
              >
                <option value="">{language === 'ar' ? 'اختر الحقل' : 'Select field'}</option>
                {availableFields.map(f => (
                  <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            {chartFilter?.field && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {(() => {
                  // Get unique values for the selected field
                  const uniqueValues = [...new Set(
                    (chartData?.data || []).map(item => item[chartFilter.field]).filter(v => v !== null && v !== undefined)
                  )];

                  // Check if values are numeric
                  const isNumeric = uniqueValues.length > 0 && uniqueValues.every(v => !isNaN(Number(v)));

                  // Check if values are dates (YYYY-MM-DD)
                  const isDate = uniqueValues.length > 0 && uniqueValues.some(v => String(v).match(/^\d{4}-\d{2}-\d{2}/));

                  // Check if values are week patterns (W01-2024, Week 1, etc.)
                  const isWeek = uniqueValues.length > 0 && uniqueValues.some(v =>
                    String(v).match(/^W\d{1,2}[-_]?\d{4}$|^Week\s*\d+/i)
                  );

                  // Check if values are month patterns (2024-01, Jan, January, etc.)
                  const isMonth = uniqueValues.length > 0 && uniqueValues.some(v =>
                    String(v).match(/^\d{4}[-/]\d{2}$|^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i)
                  );

                  // Check if field name suggests time-based data
                  const fieldLower = (chartFilter.field || '').toLowerCase();
                  const isTimeBased = isWeek || isMonth ||
                    ['week', 'month', 'year', 'quarter', 'period', 'date'].some(t => fieldLower.includes(t));

                  // For time-based fields, always use multi-select (even with many values)
                  // For other fields, limit to 30 for multi-select
                  const isTooManyValues = !isTimeBased && !isNumeric && !isDate && uniqueValues.length > 30;

                  // Parse current values as array
                  const selectedValues = chartFilter?.values || [];

                  const toggleValue = (val) => {
                    const strVal = String(val);
                    const newValues = selectedValues.includes(strVal)
                      ? selectedValues.filter(v => v !== strVal)
                      : [...selectedValues, strVal];
                    onChartFilterChange?.({ ...chartFilter, values: newValues, value: newValues.join(','), rangeMin: '', rangeMax: '' });
                  };

                  if (isNumeric) {
                    // Range inputs for numeric values
                    const numericValues = uniqueValues.map(v => Number(v));
                    const minVal = Math.min(...numericValues);
                    const maxVal = Math.max(...numericValues);

                    return (
                      <>
                        <label style={{ fontSize: '11px', color: '#718096', textTransform: 'uppercase' }}>
                          {language === 'ar'
                            ? `النطاق (الأدنى: ${minVal}، الأقصى: ${maxVal})`
                            : `Range (Min: ${minVal}, Max: ${maxVal})`}
                        </label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="number"
                            placeholder={language === 'ar' ? `الأدنى (${minVal})` : `Min (${minVal})`}
                            value={chartFilter?.rangeMin || ''}
                            onChange={(e) => onChartFilterChange?.({
                              ...chartFilter,
                              rangeMin: e.target.value,
                              value: `${e.target.value || ''}-${chartFilter?.rangeMax || ''}`,
                              values: []
                            })}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #e2e8f0',
                              fontSize: '13px',
                              width: '80px'
                            }}
                          />
                          <span style={{ color: '#718096' }}>{language === 'ar' ? 'إلى' : 'to'}</span>
                          <input
                            type="number"
                            placeholder={language === 'ar' ? `الأقصى (${maxVal})` : `Max (${maxVal})`}
                            value={chartFilter?.rangeMax || ''}
                            onChange={(e) => onChartFilterChange?.({
                              ...chartFilter,
                              rangeMax: e.target.value,
                              value: `${chartFilter?.rangeMin || ''}-${e.target.value || ''}`,
                              values: []
                            })}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #e2e8f0',
                              fontSize: '13px',
                              width: '80px'
                            }}
                          />
                        </div>
                      </>
                    );
                  } else if (isDate) {
                    // Date range inputs
                    return (
                      <>
                        <label style={{ fontSize: '11px', color: '#718096', textTransform: 'uppercase' }}>
                          {language === 'ar' ? 'نطاق التاريخ' : 'Date Range'}
                        </label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="date"
                            value={chartFilter?.rangeMin || ''}
                            onChange={(e) => onChartFilterChange?.({
                              ...chartFilter,
                              rangeMin: e.target.value,
                              value: `${e.target.value || ''}-${chartFilter?.rangeMax || ''}`,
                              values: []
                            })}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #e2e8f0',
                              fontSize: '13px'
                            }}
                          />
                          <span style={{ color: '#718096' }}>{language === 'ar' ? 'إلى' : 'to'}</span>
                          <input
                            type="date"
                            value={chartFilter?.rangeMax || ''}
                            onChange={(e) => onChartFilterChange?.({
                              ...chartFilter,
                              rangeMax: e.target.value,
                              value: `${chartFilter?.rangeMin || ''}-${e.target.value || ''}`,
                              values: []
                            })}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #e2e8f0',
                              fontSize: '13px'
                            }}
                          />
                        </div>
                      </>
                    );
                  } else if (isTooManyValues) {
                    // Text input for too many non-numeric values
                    return (
                      <>
                        <label style={{ fontSize: '11px', color: '#718096', textTransform: 'uppercase' }}>
                          {language === 'ar' ? 'تصفية (يحتوي على)' : 'Filter (contains)'}
                        </label>
                        <input
                          type="text"
                          placeholder={language === 'ar' ? 'اكتب للتصفية...' : 'Type to filter...'}
                          value={chartFilter?.value || ''}
                          onChange={(e) => onChartFilterChange?.({ ...chartFilter, value: e.target.value, values: [], rangeMin: '', rangeMax: '' })}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                            fontSize: '13px',
                            minWidth: '150px'
                          }}
                        />
                      </>
                    );
                  } else {
                    // Multi-select checkboxes for categorical values
                    // Sort values - use natural sort for time-based, alphabetical for others
                    const sortedValues = [...uniqueValues].sort((a, b) => {
                      const strA = String(a);
                      const strB = String(b);
                      // Natural sort for week patterns (W01-2024 should come before W10-2024)
                      if (isTimeBased) {
                        // Extract numbers for comparison
                        const numA = strA.match(/\d+/g)?.map(Number) || [];
                        const numB = strB.match(/\d+/g)?.map(Number) || [];
                        // Compare year first if present, then week/month number
                        for (let i = 0; i < Math.max(numA.length, numB.length); i++) {
                          const diff = (numA[i] || 0) - (numB[i] || 0);
                          if (diff !== 0) return diff;
                        }
                        return strA.localeCompare(strB);
                      }
                      return strA.localeCompare(strB);
                    });

                    const hasMany = uniqueValues.length > 20;

                    return (
                      <>
                        <label style={{ fontSize: '11px', color: '#718096', textTransform: 'uppercase' }}>
                          {language === 'ar'
                            ? `القيم (اختيار متعدد) - ${uniqueValues.length} قيمة`
                            : `Values (select multiple) - ${uniqueValues.length} values`}
                        </label>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                          padding: '8px',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          maxWidth: hasMany ? '500px' : '400px',
                          maxHeight: hasMany ? '200px' : '120px',
                          overflowY: 'auto'
                        }}>
                          {sortedValues.map(v => {
                            const strVal = String(v);
                            const isSelected = selectedValues.includes(strVal);
                            return (
                              <label
                                key={strVal}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  background: isSelected ? 'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)' : '#f7fafc',
                                  color: isSelected ? 'white' : '#4a5568',
                                  border: `1px solid ${isSelected ? 'transparent' : '#e2e8f0'}`,
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleValue(v)}
                                  style={{ display: 'none' }}
                                />
                                {strVal}
                              </label>
                            );
                          })}
                        </div>
                      </>
                    );
                  }
                })()}
              </div>
            )}
            {(chartFilter?.value || chartFilter?.rangeMin || chartFilter?.rangeMax || (chartFilter?.values && chartFilter.values.length > 0)) && (
              <button
                onClick={() => onChartFilterChange?.({})}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  fontSize: '12px',
                  cursor: 'pointer',
                  color: '#718096'
                }}
              >
                {language === 'ar' ? 'مسح' : 'Clear'}
              </button>
            )}
            <span style={{ fontSize: '11px', color: '#718096' }}>
              {language === 'ar'
                ? `عرض ${filteredCount} من ${totalCount}`
                : `Showing ${filteredCount} of ${totalCount}`}
            </span>
          </div>
        )}

        {chartData?.subtitle && (
          <div style={{
            fontSize: '13px',
            color: '#718096',
            marginBottom: '16px',
            lineHeight: '1.6',
            marginTop: '-8px'
          }}>
            {chartData.subtitle}
          </div>
        )}
        <div style={{ position: 'relative', minHeight: '300px' }}>
          {/* Loading indicator - simple, no animations that could block painting */}
          {!isChartRendered && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f7fafc',
              borderRadius: '8px'
            }}>
              <span style={{ fontSize: '14px', color: '#718096' }}>
                {language === 'ar' ? 'جاري تحميل الرسم البياني...' : 'Loading chart...'}
              </span>
            </div>
          )}
          {/* Error display */}
          {renderError && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fef2f2',
              borderRadius: '8px',
              zIndex: 2,
              padding: '20px'
            }}>
              <div style={{
                textAlign: 'center',
                color: '#dc2626',
                fontSize: '13px'
              }}>
                <div style={{ marginBottom: '8px', fontWeight: '500' }}>
                  {language === 'ar' ? 'خطأ في تحميل الرسم البياني' : 'Chart rendering error'}
                </div>
                <div style={{ fontSize: '11px', color: '#991b1b', maxWidth: '250px', wordBreak: 'break-word' }}>
                  {renderError}
                </div>
              </div>
            </div>
          )}
          <div
            ref={chartRef}
            className="chart-container"
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            style={{
              direction: language === 'ar' ? 'rtl' : 'ltr',
              minHeight: '280px'
            }}
          />
        </div>
      </ChartCard>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <ModalOverlay onClick={() => setIsFullscreen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div className="modal-title">
                {Icon && <Icon />}
                {title}
              </div>
              <CloseButton onClick={() => setIsFullscreen(false)}>
                <X />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              {chartData?.subtitle && (
                <div style={{
                  fontSize: '14px',
                  color: '#718096',
                  marginBottom: '20px',
                  lineHeight: '1.6'
                }}>
                  {chartData.subtitle}
                </div>
              )}
              <div
                ref={fullscreenChartRef}
                className="fullscreen-chart-container"
                dir={language === 'ar' ? 'rtl' : 'ltr'}
                style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}

// Filter persistence keys
const GLOBAL_FILTER_KEY = 'dashboard_global_filters';
const CHART_FILTERS_KEY = 'dashboard_chart_filters';

function Dashboard() {
  const { dbId } = useChat();
  const {
    stats,
    charts,
    summaryKpis,
    peakHours,
    loading,
    error,
    lastUpdated,
    isSyncing,
    isRebuilding,
    rebuildStatus,
    summaryTablesAvailable,
    triggerRebuild,
    fetchWithFilters,
    // Incremental refresh
    isIncrementalRefreshing,
    triggerIncrementalRefresh,
    scheduledJobs,
    refreshOptions
  } = useDashboard();

  // Settings and translation hooks
  const { topN, language, datePreset, startDate, endDate, toggleLanguage } = useSettings();
  const { t } = useTranslation();

  // Settings drawer state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Handle date preset change - fetch with new filters
  const handleDateChange = ({ preset, startDate: newStart, endDate: newEnd }) => {
    fetchWithFilters({
      datePreset: preset,
      startDate: newStart,
      endDate: newEnd,
      topN,
      language
    });
  };

  // Re-fetch when top N or language changes
  useEffect(() => {
    if (stats) {
      // Only refetch if we already have data (not on initial load)
      fetchWithFilters({
        datePreset,
        startDate,
        endDate,
        topN,
        language
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topN]);

  // Force browser reflow when charts data loads - fixes Vega rendering bug
  useEffect(() => {
    if (charts && Object.keys(charts).length > 0) {
      // Trigger resize after charts data is available to force browser repaint
      const timeoutId = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [charts]);

  // Global filters with localStorage persistence (months is now an array)
  const [globalFilters, setGlobalFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(GLOBAL_FILTER_KEY);
      const parsed = saved ? JSON.parse(saved) : { startDate: '', endDate: '', months: [] };
      // Migrate old single month to array
      if (parsed.month && !parsed.months) {
        parsed.months = [parsed.month];
        delete parsed.month;
      }
      if (!parsed.months) parsed.months = [];
      return parsed;
    } catch {
      return { startDate: '', endDate: '', months: [] };
    }
  });

  // Per-chart filters with localStorage persistence
  const [chartFilters, setChartFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(CHART_FILTERS_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem(GLOBAL_FILTER_KEY, JSON.stringify(globalFilters));
  }, [globalFilters]);

  useEffect(() => {
    localStorage.setItem(CHART_FILTERS_KEY, JSON.stringify(chartFilters));
  }, [chartFilters]);

  // Handler for updating chart-specific filters
  const handleChartFilterChange = (chartKey, filter) => {
    setChartFilters(prev => ({
      ...prev,
      [chartKey]: filter
    }));
  };

  // Reset all filters (both global and chart-specific)
  const resetAllFilters = () => {
    setGlobalFilters({ startDate: '', endDate: '', months: [] });
    setChartFilters({});
    // Also clear localStorage to prevent stale state on refresh
    localStorage.removeItem(GLOBAL_FILTER_KEY);
    localStorage.removeItem(CHART_FILTERS_KEY);
  };

  // Toggle month selection
  const toggleMonth = (month) => {
    setGlobalFilters(prev => {
      const months = prev.months || [];
      if (months.includes(month)) {
        return { ...prev, months: months.filter(m => m !== month) };
      } else {
        return { ...prev, months: [...months, month] };
      }
    });
  };

  // Check if any chart has a filter applied
  const hasAnyChartFilter = Object.values(chartFilters).some(filter =>
    filter && (filter.value || (filter.values && filter.values.length > 0) || filter.rangeMin || filter.rangeMax)
  );

  // Check if any filters are active (global OR chart-specific)
  const hasActiveFilters = globalFilters.startDate || globalFilters.endDate ||
    (globalFilters.months && globalFilters.months.length > 0) || hasAnyChartFilter;

  // Check if global date filters are active (for section headers)
  const hasGlobalDateFilters = globalFilters.startDate || globalFilters.endDate ||
    (globalFilters.months && globalFilters.months.length > 0);

  // Collapsible section states (all technical sections collapsed by default)
  const [isLookupTablesExpanded, setIsLookupTablesExpanded] = useState(false);
  const [isNullPolicyExpanded, setIsNullPolicyExpanded] = useState(false);
  const [isSchemaExpanded, setIsSchemaExpanded] = useState(false);
  const [isDataGenExpanded, setIsDataGenExpanded] = useState(false);

  // Show message for non-KFC databases
  if (dbId !== 'kfc_postgres') {
    return (
      <DashboardContainer>
        <DashboardHeader>
          <DashboardTitle>
            <BarChart3 />
            Database Dashboard
          </DashboardTitle>
          <DashboardSubtitle>
            Dashboard is currently customized for KFC Toll (PostgreSQL) database only.
          </DashboardSubtitle>
        </DashboardHeader>
      </DashboardContainer>
    );
  }

  if (loading) {
    return (
      <DashboardContainer>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e2e8f0',
            borderTopColor: '#1A8B8B',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748' }}>
            جاري تحميل بيانات لوحة المعلومات...
          </div>
          <div style={{ fontSize: '14px', color: '#718096', textAlign: 'center', maxWidth: '400px' }}>
            قاعدة البيانات تحتوي على ملايين السجلات، قد يستغرق التحميل الأول بعض الوقت
            <br />
            <span style={{ fontSize: '12px', color: '#a0aec0' }}>
              Loading millions of records, first load may take a moment...
            </span>
          </div>
        </div>
      </DashboardContainer>
    );
  }

  if (error || !stats) {
    return (
      <DashboardContainer>
        <DashboardHeader>
          <DashboardTitle>
            <BarChart3 />
            Database Dashboard
          </DashboardTitle>
          <DashboardSubtitle>
            {error || 'No data available for this database'}
          </DashboardSubtitle>
        </DashboardHeader>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      {/* Settings Drawer */}
      <DashboardSettings
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      <DashboardHeader>
        <HeaderRow>
          <div>
            <DashboardTitle>
              <Database />
              {t('dashboard.title')} - {t('dashboard.subtitle')}
            </DashboardTitle>
            <DashboardSubtitle>
              {language === 'ar'
                ? 'نظرة شاملة على بنية البيانات، التغطية، والإحصائيات'
                : 'Comprehensive overview of data structure, coverage, and statistics'}
            </DashboardSubtitle>
          </div>
          <HeaderControls>
            <LanguageToggle onClick={toggleLanguage}>
              <Globe size={16} />
              {language === 'ar' ? 'EN' : 'عربي'}
            </LanguageToggle>
            <IconButton onClick={() => setIsSettingsOpen(true)} title={t('settings.title')}>
              <Settings size={18} />
            </IconButton>
            <ExportButton />
          </HeaderControls>
        </HeaderRow>

        {lastUpdated && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '8px',
            fontSize: '12px',
            color: '#718096'
          }}>
            {isSyncing && (
              <div style={{
                width: '12px',
                height: '12px',
                border: '2px solid #e2e8f0',
                borderTopColor: '#1A8B8B',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
            <Clock style={{ width: '14px', height: '14px' }} />
            <span>
              {t('status.lastUpdated')}: {lastUpdated.toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US')}
              {isSyncing && ` (${t('status.syncing')})`}
            </span>
          </div>
        )}
      </DashboardHeader>

      {/* Summary KPI Cards - Quick Overview */}
      {summaryKpis && summaryKpis.length > 0 && (
        <KpiCardsGrid>
          {summaryKpis.map((kpi, index) => {
            // Map icon names to components
            const iconMap = {
              'activity': Activity,
              'dollar-sign': DollarSign,
              'trending-up': TrendingUp,
              'credit-card': CreditCard,
              'users': Users,
              'truck': Truck
            };
            const IconComponent = iconMap[kpi.icon] || Activity;

            // Different gradients for each card
            const gradients = [
              'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)',
              'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              'linear-gradient(135deg, #4FB8B8 0%, #1A8B8B 100%)'
            ];

            return (
              <KpiCard
                key={kpi.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                title={kpi.details || ''}
              >
                <KpiIconWrapper $gradient={gradients[index % gradients.length]}>
                  <IconComponent />
                </KpiIconWrapper>
                <KpiContent>
                  <KpiValue>{kpi.formatted}</KpiValue>
                  <KpiLabel>{language === 'ar' ? kpi.label : kpi.label_en}</KpiLabel>
                  {kpi.description && (
                    <KpiDescription>
                      {language === 'ar' ? kpi.description : kpi.description_en}
                    </KpiDescription>
                  )}
                </KpiContent>
              </KpiCard>
            );
          })}
        </KpiCardsGrid>
      )}

      {/* Peak Hours Section */}
      {peakHours && peakHours.length > 0 && (
        <PeakHoursSection>
          <PeakHoursTitle>
            <Clock />
            {language === 'ar' ? 'ساعات الذروة' : 'Peak Traffic Hours'}
          </PeakHoursTitle>
          <PeakHoursGrid>
            {peakHours.map((peak, index) => (
              <PeakHourCard key={peak.hour} $isTop={index === 0}>
                <div className="hour">{peak.hour_formatted}</div>
                <div className="count">{peak.formatted_count}</div>
                <div className="percentage">{peak.percentage}% {language === 'ar' ? 'من الإجمالي' : 'of total'}</div>
                {index === 0 && (
                  <div className="badge">{language === 'ar' ? 'الأعلى' : 'Peak'}</div>
                )}
              </PeakHourCard>
            ))}
          </PeakHoursGrid>
        </PeakHoursSection>
      )}

      {/* Key Statistics */}
      <Grid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <StatHeader>
            <StatLabel>{t('stats.totalTransactions')}</StatLabel>
            <StatIcon>
              <Database />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.totalTransactions.toLocaleString()}</StatValue>
          <StatDescription>{t('stats.totalTransactionsDesc')}</StatDescription>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <StatHeader>
            <StatLabel>{t('stats.vehicleTypes')}</StatLabel>
            <StatIcon>
              <Truck />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.vehicleTypes} {t('stats.types')}</StatValue>
          <StatDescription>{t('stats.vehicleTypesDesc')}</StatDescription>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <StatHeader>
            <StatLabel>{t('stats.paymentMethods')}</StatLabel>
            <StatIcon>
              <CreditCard />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.paymentMethods} {t('stats.methods')}</StatValue>
          <StatDescription>{t('stats.paymentMethodsDesc')}</StatDescription>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <StatHeader>
            <StatLabel>{t('stats.dateCoverage')}</StatLabel>
            <StatIcon>
              <Calendar />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.dateRange.totalDays} {t('stats.days')}</StatValue>
          <StatDescription>{stats.dateRange.min} {t('stats.dateCoverageDesc')} {stats.dateRange.max}</StatDescription>
        </StatCard>
      </Grid>

      {/* Period Comparison Section */}
      <PeriodComparison />

      {/* Charts Section */}
      {charts && (
        <>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#2d3748',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <BarChart3 color="#1A8B8B" />
            {language === 'ar' ? 'تحليلات لوحة المعلومات' : 'Dashboard Analytics'}
          </div>

          {/* Combined Filters Section for Time-Series Charts */}
          <FilterSection>
            <FilterHeader>
              <div className="filter-title">
                <Filter />
                {language === 'ar' ? 'فلاتر الرسوم البيانية الزمنية' : 'Time-Series Charts Filters'}
              </div>
              <div className="filter-actions">
                <FilterButton
                  className="reset-btn"
                  onClick={resetAllFilters}
                  disabled={!hasActiveFilters}
                  title={language === 'ar' ? 'مسح جميع الفلاتر' : 'Clear all filters'}
                >
                  <RotateCcw />
                  {language === 'ar' ? 'مسح الفلاتر' : 'Reset All'}
                </FilterButton>
              </div>
            </FilterHeader>

            <div style={{ fontSize: '13px', color: '#718096', marginBottom: '16px', padding: '8px 12px', background: '#edf2f7', borderRadius: '8px' }}>
              {language === 'ar'
                ? <><strong>ملاحظة:</strong> تنطبق هذه الفلاتر على <strong>الرسوم البيانية الزمنية</strong> أدناه (الاتجاه اليومي، الاتجاه الأسبوعي، المقارنة الشهرية، أكثر الأيام ازدحاماً).</>
                : <><strong>Note:</strong> These filters apply to <strong>Time-Series Charts</strong> below (Daily Trend, Weekly Trend, Monthly Comparison, Top Busiest Days).</>
              }
            </div>

            {/* Filter Type 1: Quick Date Presets (Server-side) */}
            <div style={{ marginBottom: '20px', padding: '16px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Database size={16} color="#15803d" />
                <span style={{ fontWeight: '600', color: '#15803d', fontSize: '14px' }}>
                  {language === 'ar' ? 'الفلاتر السريعة (من قاعدة البيانات)' : 'Quick Presets (Database-level)'}
                </span>
              </div>
              <div style={{ fontSize: '12px', color: '#166534', marginBottom: '12px' }}>
                {language === 'ar'
                  ? '⚡ فلترة سريعة - يتم جلب البيانات المفلترة مباشرة من قاعدة البيانات'
                  : '⚡ Fast filtering - fetches only filtered data from database'}
              </div>
              <DatePresets onChange={handleDateChange} />
            </div>

            {/* Filter Type 2: Custom Date Range (Client-side) */}
            <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Calendar size={16} color="#1d4ed8" />
                <span style={{ fontWeight: '600', color: '#1d4ed8', fontSize: '14px' }}>
                  {language === 'ar' ? 'فلاتر إضافية (تصفية محلية)' : 'Additional Filters (Client-side)'}
                </span>
              </div>
              <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '12px' }}>
                {language === 'ar'
                  ? '🔍 تصفية إضافية للبيانات المحملة - تعمل مع الفلاتر السريعة أعلاه'
                  : '🔍 Additional filtering of loaded data - works with quick presets above'}
              </div>

            <FilterGrid>
              <FilterGroup>
                <label>{language === 'ar' ? 'تاريخ البداية' : 'Start Date'}</label>
                <input
                  type="date"
                  value={globalFilters.startDate}
                  onChange={(e) => setGlobalFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </FilterGroup>
              <FilterGroup>
                <label>{language === 'ar' ? 'تاريخ النهاية' : 'End Date'}</label>
                <input
                  type="date"
                  value={globalFilters.endDate}
                  onChange={(e) => setGlobalFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </FilterGroup>
            </FilterGrid>

            <div style={{ marginTop: '16px' }}>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>
                {language === 'ar' ? 'الأشهر (اختيار متعدد)' : 'Months (select multiple)'}
              </label>
              <MonthCheckboxGrid>
                {(language === 'ar'
                  ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
                  : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                ).map((month, idx) => (
                  <MonthCheckbox
                    key={idx}
                    checked={(globalFilters.months || []).includes(String(idx + 1))}
                  >
                    <input
                      type="checkbox"
                      checked={(globalFilters.months || []).includes(String(idx + 1))}
                      onChange={() => toggleMonth(String(idx + 1))}
                    />
                    {month}
                  </MonthCheckbox>
                ))}
              </MonthCheckboxGrid>
            </div>
            </div>

            {/* Active Filters Display */}
            {hasActiveFilters && (
              <ActiveFiltersBar>
                <span className="active-filters-label">
                  <Filter />
                  {language === 'ar' ? 'الفلاتر النشطة:' : 'Active Filters:'}
                </span>
                {globalFilters.startDate && (
                  <FilterTag>
                    {language === 'ar' ? 'من:' : 'From:'} {globalFilters.startDate}
                    <button
                      onClick={() => setGlobalFilters(prev => ({ ...prev, startDate: '' }))}
                      title={language === 'ar' ? 'إزالة هذا الفلتر' : 'Remove this filter'}
                    >
                      <X />
                    </button>
                  </FilterTag>
                )}
                {globalFilters.endDate && (
                  <FilterTag>
                    {language === 'ar' ? 'إلى:' : 'To:'} {globalFilters.endDate}
                    <button
                      onClick={() => setGlobalFilters(prev => ({ ...prev, endDate: '' }))}
                      title={language === 'ar' ? 'إزالة هذا الفلتر' : 'Remove this filter'}
                    >
                      <X />
                    </button>
                  </FilterTag>
                )}
                {(globalFilters.months || []).length > 0 && (
                  <FilterTag>
                    {language === 'ar' ? 'الأشهر:' : 'Months:'} {(globalFilters.months || []).map(m =>
                      language === 'ar'
                        ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'][parseInt(m) - 1]
                        : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][parseInt(m) - 1]
                    ).join(', ')}
                    <button
                      onClick={() => setGlobalFilters(prev => ({ ...prev, months: [] }))}
                      title={language === 'ar' ? 'إزالة هذا الفلتر' : 'Remove this filter'}
                    >
                      <X />
                    </button>
                  </FilterTag>
                )}
              </ActiveFiltersBar>
            )}
          </FilterSection>

          {/* REBUILD SECTION - Summary Tables Status */}
          <RebuildSection $active={summaryTablesAvailable}>
            <div className="rebuild-info">
              <div className="title">
                <Database size={16} />
                {language === 'ar' ? 'حالة الأداء' : 'Performance Status'}
              </div>
              <div className="status">
                {summaryTablesAvailable ? (
                  <>
                    <Zap size={14} className="status-icon active" />
                    <span>{language === 'ar' ? 'الاستعلامات السريعة مفعّلة' : 'Fast queries enabled'}</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={14} className="status-icon inactive" />
                    <span>{language === 'ar' ? 'استعلامات مباشرة (أبطأ)' : 'Direct queries (slower)'}</span>
                  </>
                )}
              </div>
              {rebuildStatus && (
                <div className="last-rebuild">
                  {language === 'ar' ? 'آخر تحديث:' : 'Updated:'} {new Date(rebuildStatus.rebuilt_at).toLocaleString()}
                  <span className="duration">({rebuildStatus.duration_seconds?.toFixed(1)}s)</span>
                </div>
              )}
              {/* Scheduled Jobs Info */}
              {scheduledJobs && (
                <ScheduledJobsInfo $active={summaryTablesAvailable}>
                  {scheduledJobs.daily_incremental && (
                    <div className="job-item">
                      <Clock size={12} />
                      <span className="job-name">{language === 'ar' ? 'تحديث يومي:' : 'Daily:'}</span>
                      <span className="job-time">
                        {new Date(scheduledJobs.daily_incremental.next_run).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
                          weekday: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                  {scheduledJobs.weekly_full && (
                    <div className="job-item">
                      <Calendar size={12} />
                      <span className="job-name">{language === 'ar' ? 'تحديث أسبوعي:' : 'Weekly:'}</span>
                      <span className="job-time">
                        {new Date(scheduledJobs.weekly_full.next_run).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                </ScheduledJobsInfo>
              )}
            </div>
            <RefreshButtonsContainer>
              <QuickRefreshButton
                onClick={triggerIncrementalRefresh}
                disabled={isIncrementalRefreshing || isRebuilding}
                title={language === 'ar' ? 'تحديث سريع (~5 ثواني)' : 'Quick refresh (~5 seconds)'}
              >
                <Zap className={isIncrementalRefreshing ? 'spinning' : ''} size={16} />
                {isIncrementalRefreshing
                  ? (language === 'ar' ? 'جاري...' : 'Running...')
                  : (language === 'ar' ? 'تحديث سريع' : 'Quick Refresh')}
              </QuickRefreshButton>
              <RebuildButton
                onClick={triggerRebuild}
                disabled={isRebuilding || isIncrementalRefreshing}
                title={language === 'ar' ? 'تحديث كامل (~15 دقيقة)' : 'Full refresh (~15 minutes)'}
              >
                <RefreshCw className={isRebuilding ? 'spinning' : ''} size={16} />
                {isRebuilding
                  ? (language === 'ar' ? 'جاري التحديث...' : 'Rebuilding...')
                  : (language === 'ar' ? 'تحديث كامل' : 'Full Refresh')}
              </RebuildButton>
            </RefreshButtonsContainer>
          </RebuildSection>

          {/* Data Quality Insights Panel */}
          <DataQualityInsights />

          {/* TIME-SERIES CHARTS - Affected by Global Date Filters */}
          <ChartSectionHeader filtered={hasGlobalDateFilters}>
            <div className="section-icon">
              <TrendingUp />
            </div>
            <div className="section-info">
              <h3>{language === 'ar' ? 'الرسوم البيانية الزمنية' : 'Time-Series Charts'}</h3>
              <p>
                {hasGlobalDateFilters
                  ? (language === 'ar' ? '⚡ تعرض هذه الرسوم البيانات حسب الفلاتر المحددة أعلاه' : '⚡ Showing filtered data based on selected filters above')
                  : (language === 'ar' ? 'استخدم فلاتر التاريخ أعلاه لتصفية هذه الرسوم' : 'Use date filters above to filter these charts')
                }
              </p>
            </div>
            {hasGlobalDateFilters && (
              <span className="section-badge">
                <Filter size={12} />
                {language === 'ar' ? 'مفلتر' : 'Filtered'}
              </span>
            )}
          </ChartSectionHeader>

          <ChartsGrid>
            <Chart
              title={t('chart.weeklyTrend')}
              description={t('chart.weeklyTrendDesc')}
              chartData={charts.weekly_trend}
              icon={BarChart3}
              globalFilters={globalFilters}
              chartFilter={chartFilters['weekly_trend']}
              onChartFilterChange={(f) => handleChartFilterChange('weekly_trend', f)}
              t={t}
              language={language}
            />
            <Chart
              title={t('chart.monthlyComparison')}
              description={t('chart.monthlyComparisonDesc')}
              chartData={charts.monthly_comparison}
              icon={Calendar}
              globalFilters={globalFilters}
              chartFilter={chartFilters['monthly_comparison']}
              onChartFilterChange={(f) => handleChartFilterChange('monthly_comparison', f)}
              t={t}
              language={language}
            />
            <Chart
              title={t('chart.topBusiestDays')}
              description={t('chart.topBusiestDaysDesc')}
              chartData={charts.top_busiest_days}
              icon={Award}
              globalFilters={globalFilters}
              chartFilter={chartFilters['top_busiest_days']}
              onChartFilterChange={(f) => handleChartFilterChange('top_busiest_days', f)}
              t={t}
              language={language}
            />
          </ChartsGrid>

          {/* REVENUE & TRAVELER INSIGHTS - New Summary Table Charts */}
          <div style={{ marginTop: '32px' }}>
            <ChartSectionHeader>
              <div className="section-icon">
                <DollarSign />
              </div>
              <div className="section-info">
                <h3>{language === 'ar' ? 'رؤى الإيرادات والمسافرين' : 'Revenue & Traveler Insights'}</h3>
                <p>{language === 'ar' ? 'بيانات مجمعة مسبقاً من جداول الملخصات للأداء السريع' : 'Pre-aggregated data from summary tables for fast performance'}</p>
              </div>
              {summaryTablesAvailable && <span className="section-badge">{language === 'ar' ? 'محسّن' : 'Optimized'}</span>}
            </ChartSectionHeader>

            <ChartsGrid>
              {charts.frequent_vehicles && (
                <Chart
                  title={t('chart.frequentVehicles')}
                  description={language === 'ar' ? 'أعلى 10 مركبات من حيث عدد العبور الكلي (جميع الاتجاهات)' : 'Top 10 vehicles by total crossing count (all directions)'}
                  chartData={charts.frequent_vehicles}
                  icon={Truck}
                  globalFilters={{}}
                  chartFilter={chartFilters['frequent_vehicles']}
                  onChartFilterChange={(f) => handleChartFilterChange('frequent_vehicles', f)}
                  t={t}
                  language={language}
                />
              )}
              {charts.bidirectional_travelers && (
                <Chart
                  title={t('chart.bidirectionalTravelers')}
                  description={language === 'ar' ? 'أعلى 10 مسافرين عبروا بالاتجاهين (السعودية ↔ البحرين) 4 مرات أو أكثر' : 'Top 10 travelers who crossed both directions (Saudi ↔ Bahrain) 4+ times'}
                  chartData={charts.bidirectional_travelers}
                  icon={Users}
                  globalFilters={{}}
                  chartFilter={chartFilters['bidirectional_travelers']}
                  onChartFilterChange={(f) => handleChartFilterChange('bidirectional_travelers', f)}
                  t={t}
                  language={language}
                />
              )}
            </ChartsGrid>
          </div>

          {/* SUMMARY CHARTS - Use Per-Chart Filters */}
          <div style={{ marginTop: '32px' }}>
            <ChartSectionHeader>
              <div className="section-icon">
                <PieChart />
              </div>
              <div className="section-info">
                <h3>{language === 'ar' ? 'الرسوم البيانية التجميعية' : 'Summary Charts'}</h3>
                <p>{language === 'ar' ? 'استخدم زر الفلتر في كل رسم بياني للتصفية المحددة' : 'Use the filter button on each chart for specific filtering'}</p>
              </div>
            </ChartSectionHeader>

            <ChartsGrid>
              <Chart
                title={t('chart.vehicleDistribution')}
                description={t('chart.vehicleDistributionDesc')}
                chartData={charts.vehicle_distribution}
                icon={Truck}
                globalFilters={{}}
                chartFilter={chartFilters['vehicle_distribution']}
                onChartFilterChange={(f) => handleChartFilterChange('vehicle_distribution', f)}
                t={t}
                language={language}
              />
              <Chart
                title={t('chart.paymentMethods')}
                description={t('chart.paymentMethodsDesc')}
                chartData={charts.payment_methods}
                icon={CreditCard}
                globalFilters={{}}
                chartFilter={chartFilters['payment_methods']}
                onChartFilterChange={(f) => handleChartFilterChange('payment_methods', f)}
                t={t}
                language={language}
              />
              <Chart
                title={t('chart.directionSplit')}
                description={t('chart.directionSplitDesc')}
                chartData={charts.direction_split}
                icon={MapPin}
                globalFilters={{}}
                chartFilter={chartFilters['direction_split']}
                onChartFilterChange={(f) => handleChartFilterChange('direction_split', f)}
                t={t}
                language={language}
              />
              <Chart
                title={t('chart.hourlyTraffic')}
                description={t('chart.hourlyTrafficDesc')}
                chartData={charts.hourly_traffic}
                icon={Activity}
                globalFilters={{}}
                chartFilter={chartFilters['hourly_traffic']}
                onChartFilterChange={(f) => handleChartFilterChange('hourly_traffic', f)}
                t={t}
                language={language}
              />
              {charts.hourly_vehicle_traffic && (
                <Chart
                  title={language === 'ar' ? 'حركة المرور بالساعة حسب نوع المركبة' : 'Hourly Traffic by Vehicle Type'}
                  description={language === 'ar' ? 'توزيع الحركة بالساعة لكل نوع مركبة' : 'Hourly traffic distribution by vehicle type'}
                  chartData={charts.hourly_vehicle_traffic}
                  icon={Activity}
                  globalFilters={{}}
                  chartFilter={chartFilters['hourly_vehicle_traffic']}
                  onChartFilterChange={(f) => handleChartFilterChange('hourly_vehicle_traffic', f)}
                  t={t}
                  language={language}
                />
              )}
              <Chart
                title={t('chart.shiftDistribution')}
                description={t('chart.shiftDistributionDesc')}
                chartData={charts.shift_distribution}
                icon={Clock}
                globalFilters={{}}
                chartFilter={chartFilters['shift_distribution']}
                onChartFilterChange={(f) => handleChartFilterChange('shift_distribution', f)}
                t={t}
                language={language}
              />
              <Chart
                title={t('chart.laneUsage')}
                description={t('chart.laneUsageDesc')}
                chartData={charts.lane_usage}
                icon={Hash}
                globalFilters={{}}
                chartFilter={chartFilters['lane_usage']}
                onChartFilterChange={(f) => handleChartFilterChange('lane_usage', f)}
                t={t}
                language={language}
              />
              <Chart
                title={language === 'ar' ? 'أنواع المركبات حسب الاتجاه' : 'Vehicle Types by Direction'}
                description={language === 'ar' ? 'توزيع أنواع المركبات حسب اتجاه السفر' : 'Vehicle type distribution by travel direction'}
                chartData={charts.vehicle_by_direction}
                icon={GitBranch}
                globalFilters={{}}
                chartFilter={chartFilters['vehicle_by_direction']}
                onChartFilterChange={(f) => handleChartFilterChange('vehicle_by_direction', f)}
                t={t}
                language={language}
              />
              <Chart
                title={t('chart.exemptions')}
                description={t('chart.exemptionsDesc')}
                chartData={charts.exemption_distribution}
                icon={PieChart}
                globalFilters={{}}
                chartFilter={chartFilters['exemption_distribution']}
                onChartFilterChange={(f) => handleChartFilterChange('exemption_distribution', f)}
                t={t}
                language={language}
              />
            </ChartsGrid>
          </div>
        </>
      )}

      {/* MY PINNED CHARTS SECTION */}
      <MyChartsSection />

      {/* TECHNICAL REFERENCE SECTION - Compact with Full Details */}
      <div style={{ marginTop: '24px' }}>
        {/* Section Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '16px',
          padding: '0 4px'
        }}>
          <Info size={16} color="#64748b" />
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
            {language === 'ar' ? 'مرجع تقني للمطورين' : 'Technical Reference (for developers)'}
          </span>
        </div>

        {/* Card 1: Lookup Tables - Purple gradient */}
        <div style={{
          background: 'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)',
          borderRadius: '12px',
          padding: '16px 20px',
          color: 'white',
          marginBottom: '16px',
          boxShadow: '0 4px 12px rgba(26, 139, 139, 0.2)'
        }}>
          <div
            onClick={() => setIsLookupTablesExpanded(!isLookupTablesExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Database size={18} />
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  {language === 'ar' ? 'جداول البحث الأصلية' : 'Original Lookup Tables'}
                </div>
                <div style={{ fontSize: '11px', opacity: 0.85 }}>
                  {language === 'ar' ? '6 جداول - جميع المعرفات محولة لنص' : '6 tables - All IDs denormalized to text'}
                </div>
              </div>
            </div>
            {isLookupTablesExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
          {isLookupTablesExpanded && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {stats.lookupTables.map((table, idx) => (
                  <div key={idx} style={{
                    background: 'rgba(255,255,255,0.12)',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#fbbf24', marginBottom: '4px', textTransform: 'uppercase' }}>
                      {table.name}
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.85, marginBottom: '6px' }}>
                      {table.source} • {table.count} values
                    </div>
                    {table.values && table.values.length > 0 && (
                      <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '4px', padding: '8px', maxHeight: '150px', overflowY: 'auto' }}>
                        {table.values.map((v, vIdx) => (
                          <div key={vIdx} style={{ fontSize: '12px', fontFamily: 'monospace', padding: '3px 0', display: 'flex', gap: '6px' }}>
                            <span style={{ color: '#fbbf24' }}>{v.id}</span>
                            <span style={{ opacity: 0.5 }}>→</span>
                            <span>{v.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '12px', padding: '12px 14px', background: 'rgba(255,255,255,0.1)', borderRadius: '6px', borderLeft: '3px solid #fbbf24' }}>
                <div style={{ fontSize: '13px', lineHeight: 1.5 }}>
                  <strong>{language === 'ar' ? 'استراتيجية التبسيط:' : 'Denormalization:'}</strong>{' '}
                  {language === 'ar'
                    ? 'جميع المعرفات محولة لنص (مثال: VEHICLECLASSID=2 → "CAR"). لا حاجة لـ JOINs.'
                    : 'All IDs converted to TEXT (e.g., VEHICLECLASSID=2 → "CAR"). No JOINs needed.'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Card 2: NULL Policy - White card with warning colors */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0'
        }}>
          <div
            onClick={() => setIsNullPolicyExpanded(!isNullPolicyExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertCircle size={18} color="#f59e0b" />
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                  {language === 'ar' ? 'سياسة الحقول الفارغة (NULL)' : 'NULL Field Policy'}
                </div>
                <div style={{ fontSize: '11px', color: '#718096' }}>
                  {language === 'ar' ? 'أي البيانات يمكن أن تكون فارغة؟' : 'Which data can be NULL?'}
                </div>
              </div>
            </div>
            {isNullPolicyExpanded ? <ChevronUp size={18} color="#94a3b8" /> : <ChevronDown size={18} color="#94a3b8" />}
          </div>
          {isNullPolicyExpanded && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '10px' }}>
                {stats.nullableFields.map((field, idx) => (
                  <div key={idx} style={{
                    background: 'linear-gradient(135deg, #fef5e7 0%, #fef9e6 100%)',
                    borderRadius: '8px',
                    padding: '12px',
                    borderLeft: '3px solid #f59e0b'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <AlertCircle size={16} color="#f59e0b" />
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>{field.field}</span>
                    </div>
                    {field.null_percentage && (
                      <div style={{ fontSize: '13px', color: '#d97706', fontWeight: '500', marginBottom: '6px' }}>
                        NULL: {field.null_percentage}
                      </div>
                    )}
                    {field.reason && (
                      <div style={{ fontSize: '12px', color: '#4a5568', lineHeight: 1.4, marginBottom: '8px' }}>
                        {field.reason}
                      </div>
                    )}
                    {field.example && (
                      <div style={{ background: 'white', padding: '8px 10px', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace', color: '#2d3748' }}>
                        <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '11px' }}>Example: </span>
                        {field.example}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '12px', padding: '12px 14px', background: '#fef3c7', borderRadius: '6px', borderLeft: '3px solid #f59e0b' }}>
                <div style={{ fontSize: '13px', color: '#92400e', lineHeight: 1.5 }}>
                  <strong>{language === 'ar' ? 'مهم:' : 'Important:'}</strong>{' '}
                  {language === 'ar'
                    ? 'جميع الحقول الأخرى (غير المذكورة) لا تقبل NULL أبداً.'
                    : 'All other fields (not listed) are NEVER NULL.'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Card 3: Schema - White card */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0'
        }}>
          <div
            onClick={() => setIsSchemaExpanded(!isSchemaExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Layers size={18} color="#1A8B8B" />
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                  {language === 'ar' ? 'هيكل قاعدة البيانات' : 'Database Schema'}
                </div>
                <div style={{ fontSize: '11px', color: '#718096' }}>
                  {language === 'ar' ? 'جدول واحد مبسط مع 6 جداول بحث مدمجة' : 'Single denormalized table with 6 integrated lookups'}
                </div>
              </div>
            </div>
            {isSchemaExpanded ? <ChevronUp size={18} color="#94a3b8" /> : <ChevronDown size={18} color="#94a3b8" />}
          </div>
          {isSchemaExpanded && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px' }}>
                {stats.schema.categories.map((cat, idx) => (
                  <div key={idx} style={{
                    background: '#f8fafc',
                    borderRadius: '6px',
                    padding: '12px 14px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1A8B8B', marginBottom: '4px' }}>
                      {cat.name}
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#2d3748' }}>
                      {cat.count} <span style={{ fontSize: '12px', fontWeight: '400', color: '#718096' }}>cols</span>
                    </div>
                    {cat.fields && (
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', lineHeight: 1.4 }}>
                        {cat.fields}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '12px', padding: '12px 14px', background: '#f0f9ff', borderRadius: '6px', borderLeft: '3px solid #1A8B8B' }}>
                <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: 1.5 }}>
                  <strong>{language === 'ar' ? 'تصميم جدول واحد:' : 'Single Table Design:'}</strong>{' '}
                  {language === 'ar'
                    ? 'جميع البيانات في جدول toll_transactions (24 عمود). لا حاجة لـ JOINs.'
                    : 'All data in toll_transactions (24 columns). No JOINs needed.'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Card 4: Data Generation - Collapsible, for testing reference */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '16px 20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0'
        }}>
          <div
            onClick={() => setIsDataGenExpanded(!isDataGenExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <TrendingUp size={18} color="#6366f1" />
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                  {language === 'ar' ? 'استراتيجية توليد البيانات' : 'Data Generation Strategy'}
                </div>
                <div style={{ fontSize: '11px', color: '#718096' }}>
                  {language === 'ar' ? 'للاختبار فقط - توزيعات احتمالية واقعية' : 'Testing only - Realistic probability distributions'}
                </div>
              </div>
            </div>
            {isDataGenExpanded ? <ChevronUp size={18} color="#94a3b8" /> : <ChevronDown size={18} color="#94a3b8" />}
          </div>
          {isDataGenExpanded && stats.dataGeneration && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ padding: '10px 14px', background: '#fef5e7', borderRadius: '6px', borderLeft: '3px solid #f59e0b', marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', color: '#92400e' }}>
                  <strong>Note:</strong> {language === 'ar'
                    ? 'هذا القسم يوثق كيفية توليد بيانات الاختبار.'
                    : 'This section documents how synthetic test data was generated.'}
                </div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                borderRadius: '8px',
                padding: '14px',
                color: 'white'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                  {stats.dataGeneration.distributions && stats.dataGeneration.distributions.map((dist, idx) => (
                    <div key={idx} style={{
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '6px',
                      padding: '12px',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {dist.icon === 'Truck' && <Truck size={14} color="#fbbf24" />}
                        {dist.icon === 'CreditCard' && <CreditCard size={14} color="#fbbf24" />}
                        {dist.icon === 'MapPin' && <MapPin size={14} color="#fbbf24" />}
                        {dist.icon === 'Calendar' && <Calendar size={14} color="#fbbf24" />}
                        {dist.icon === 'AlertCircle' && <AlertCircle size={14} color="#fbbf24" />}
                        {dist.icon === 'Activity' && <Activity size={14} color="#fbbf24" />}
                        {dist.name}
                      </div>
                      <div style={{ fontSize: '12px', opacity: 0.85, marginBottom: '8px', lineHeight: 1.4 }}>
                        {dist.description}
                      </div>
                      {dist.breakdown && dist.breakdown.length > 0 && (
                        <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '4px', padding: '8px', maxHeight: '120px', overflowY: 'auto' }}>
                          {dist.breakdown.map((item, bIdx) => (
                            <div key={bIdx} style={{ fontSize: '11px', fontFamily: 'monospace', padding: '2px 0' }}>
                              • {item}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* END TECHNICAL REFERENCE SECTION */}

    </DashboardContainer>
  );
}

export default Dashboard;
